import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { companyApi } from '../features/company/api/companyApi';
import { vehiclesApi } from '../features/vehicles/api/vehicleApi';
import { driversApi } from '../features/drivers/api/driverApi';
import { freightApi } from 'src/features/freight/api/freightApi';
import { quotesApi } from 'src/features/quotes/api/quotesApi';
import { applicationApi } from 'src/features/application/api/applicationApi';
import { lossApi } from 'src/features/application/api/lossApi'; // Import Loss API
import { carrierEngineApi } from 'src/features/carrier/carrierEngineApi'; // Import Carrier Engine API
import quotesReducer from 'src/features/quotes/slice/quoteSlice';
import vehiclesReducer from '../features/vehicles/slice/vehicleSlice';
import companyReducer from '../features/company/slice/companySlice';
import UIReducer from '../slices/UiSlice';
import driversReducer from '../features/drivers/slice/driverSlice';
import freightReducer from 'src/features/freight/slice/freightSlice';
import applicationReducer from 'src/features/application/slice/applicationSlice';
import lossReducer from 'src/features/application/slice/lossSlice'; // Import Loss Slice
import userReducer from 'src/features/user/slice/userSlice';
import { userApi } from 'src/features/user/api/userApi';
import { authenticationApi } from 'src/features/authentication/api/authenticationApi';
import progressReducer from 'src/slices/progressSlice';
import { tourApi } from 'src/features/tour/api/tourApi';
import tourReducer from 'src/features/tour/slice/tourSlice';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // Default: localStorage for web

// Persist config for companySlice
const PersistConfig = {
  company: {
    key: 'company',
    storage,
  },
  driver: {
    key: 'driver',
    storage,
  },
  vehicles: {
    key: 'vehicles',
    storage,
  },
  freight: {
    key: 'freight',
    storage,
  },
  UI: {
    key: 'UI',
    storage,
  },
  quotes: {
    key: 'quotes',
    storage,
  },
  application: {
    key: 'application',
    storage,
  },
  users: {
    key: 'users',
    storage,
  },
  loss: {
    key: 'loss',
    storage, // Add persistence for Loss
  },
  tour: {
    key: 'tour',
    storage, // Persist tour progress
  },
};

// Persisted reducers
const persistedCompanyReducer = persistReducer(PersistConfig.company, companyReducer);
const persistedVehiclesReducer = persistReducer(PersistConfig.vehicles, vehiclesReducer);
const persistedDriversReducer = persistReducer(PersistConfig.driver, driversReducer);
const persistedFreightReducer = persistReducer(PersistConfig.freight, freightReducer);
const persistedUIReducer = persistReducer(PersistConfig.UI, UIReducer);
const persistedQuotesReducer = persistReducer(PersistConfig.quotes, quotesReducer);
const persistedApplicationReducer = persistReducer(PersistConfig.application, applicationReducer);
const persistedUserReducer = persistReducer(PersistConfig.users, userReducer);
const persistedLossReducer = persistReducer(PersistConfig.loss, lossReducer); // Persist Loss Reducer
const persistedTourReducer = persistReducer(PersistConfig.tour, tourReducer); // Persist Tour Reducer

// Combine reducers
const rootReducer = combineReducers({
  company: persistedCompanyReducer,
  vehicles: persistedVehiclesReducer,
  drivers: persistedDriversReducer,
  freights: persistedFreightReducer,
  UI: persistedUIReducer,
  quotes: persistedQuotesReducer,
  application: persistedApplicationReducer,
  users: persistedUserReducer,
  loss: persistedLossReducer, // Add Loss reducer to root
  tour: persistedTourReducer, // Add Tour reducer to root
  progress: progressReducer,
  [companyApi.reducerPath]: companyApi.reducer,
  [tourApi.reducerPath]: tourApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [vehiclesApi.reducerPath]: vehiclesApi.reducer,
  [driversApi.reducerPath]: driversApi.reducer,
  [freightApi.reducerPath]: freightApi.reducer,
  [quotesApi.reducerPath]: quotesApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [lossApi.reducerPath]: lossApi.reducer, // Add Loss API reducer path
  [authenticationApi.reducerPath]: authenticationApi.reducer,
  [carrierEngineApi.reducerPath]: carrierEngineApi.reducer, // Add Carrier Engine API reducer path
});

// Configure the store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(companyApi.middleware)
      .concat(userApi.middleware)
      .concat(vehiclesApi.middleware)
      .concat(driversApi.middleware)
      .concat(freightApi.middleware)
      .concat(quotesApi.middleware)
      .concat(applicationApi.middleware)
      .concat(lossApi.middleware)
      .concat(authenticationApi.middleware)
      .concat(carrierEngineApi.middleware)
      .concat(tourApi.middleware),
});

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Persistor
export const persistor = persistStore(store);

export default store;
